import {useCallback, useMemo} from "react";
import {useLocation, useNavigate} from "react-router-dom";

import {ComboboxGeneric} from "@/components/ui/combobox-generic";
import {Item, ItemContent, ItemDescription, ItemTitle} from "@/components/ui/item";
import type {Property} from "@/src/modules/properties/types";
import {cn} from "@/lib/utils";

import {
  getPropertyMemberships,
  getResidentMembershipRoleLabel,
  residentNeedsContextSwitcher,
  residentPropertyNeedsSwitcher,
  sortResidentProperties,
} from "../lib/resident-membership";
import {residentContextService} from "../services/resident-context";
import {useStoredMe} from "../store/me";
import {useResidentContextStore} from "../store/resident-context";
import {useStoredUserProperty} from "../store/user-property";
import type {ResidentMembership} from "../types/resident-membership";
import {getResidentNavigationPathBeforeContextChange} from "@/src/modules/routing/lib/resident-board-module-paths";

type ResidentContextSwitcherProps = {
  className?: string;
  /** Compact layout for desktop header inline cluster. */
  variant?: "banner" | "inline";
};

export function ResidentContextSwitcher({
  className,
  variant = "banner",
}: ResidentContextSwitcherProps) {
  const me = useStoredMe();
  const navigate = useNavigate();
  const location = useLocation();
  const userProperty = useStoredUserProperty();
  const selectedHouseholdId = useResidentContextStore((s) => s.selectedHouseholdId);

  const properties = useMemo(
    () => (me?.isResident ? sortResidentProperties(me.properties) : []),
    [me?.isResident, me?.properties],
  );

  const selectedProperty = userProperty;
  const memberships = useMemo(
    () => (selectedProperty ? getPropertyMemberships(selectedProperty) : []),
    [selectedProperty],
  );

  const propertyValue = useMemo(() => {
    if (!selectedProperty?.id || !properties.length) return null;
    return properties.find((p) => String(p.id) === String(selectedProperty.id)) ?? null;
  }, [properties, selectedProperty?.id]);

  const membershipValue = useMemo(() => {
    if (!selectedHouseholdId || !memberships.length) return null;
    return (
      memberships.find((m) => String(m.house_hold_id) === String(selectedHouseholdId)) ?? null
    );
  }, [memberships, selectedHouseholdId]);

  const isPropertyEqual = useCallback(
    (item: Property, selected: Property | null) => String(item.id) === String(selected?.id),
    [],
  );

  const isMembershipEqual = useCallback(
    (item: ResidentMembership, selected: ResidentMembership | null) =>
      String(item.house_hold_id) === String(selected?.house_hold_id),
    [],
  );

  if (!me?.isResident || !residentNeedsContextSwitcher(me)) {
    return null;
  }

  const showPropertySwitcher = properties.length > 1;
  const showUnitSwitcher = selectedProperty
    ? residentPropertyNeedsSwitcher(selectedProperty)
    : false;

  const comboboxClassName = cn(
    "min-w-0 flex-1 whitespace-nowrap",
    variant === "inline" && "max-w-48",
  );

  return (
    <div
      className={cn(
        "flex min-w-0 flex-col gap-2",
        variant === "inline" && "flex-row flex-wrap items-center gap-2",
        className,
      )}
    >
      {showPropertySwitcher ? (
        <ComboboxGeneric<Property>
          showClear={false}
          items={properties}
          className={comboboxClassName}
          preserveCase
          placeholder="Property"
          getItemKey={(item) => item.id}
          getItemLabel={(item) => item.name}
          isItemEqual={isPropertyEqual}
          renderItem={(option) => (
            <Item size="sm" className="p-0">
              <ItemContent>
                <ItemTitle className="font-normal">{option.name}</ItemTitle>
              </ItemContent>
            </Item>
          )}
          value={propertyValue}
          onChange={(value, eventDetails) => {
            if (!value || eventDetails.reason === "none") return;
            if (userProperty && String(userProperty.id) === String(value.id)) return;

            const nextPath = getResidentNavigationPathBeforeContextChange({
              pathname: location.pathname,
              me,
              nextPropertyId: value.id,
            });
            if (nextPath) {
              navigate(nextPath, {replace: true});
            }

            residentContextService.setProperty(me, value);
          }}
        />
      ) : null}

      {showUnitSwitcher ? (
        <ComboboxGeneric<ResidentMembership>
          showClear={false}
          items={memberships}
          className={comboboxClassName}
          preserveCase
          placeholder="Unit"
          getItemKey={(item) => item.house_hold_id}
          getItemLabel={(item) => item.house_hold_name}
          isItemEqual={isMembershipEqual}
          renderItem={(option) => (
            <Item size="sm" className="p-0">
              <ItemContent>
                <ItemTitle className="font-normal">{option.house_hold_name}</ItemTitle>
                <ItemDescription>{getResidentMembershipRoleLabel(option)}</ItemDescription>
              </ItemContent>
            </Item>
          )}
          value={membershipValue}
          onChange={(value, eventDetails) => {
            if (!value || eventDetails.reason === "none") return;
            if (String(value.house_hold_id) === String(selectedHouseholdId)) return;
            residentContextService.setMembership(me, value);
          }}
        />
      ) : null}
    </div>
  );
}
